<script lang="ts">
  import BrandMark from '../../components/BrandMark.svelte'
  import ThemeToggle from '../../components/ThemeToggle.svelte'
</script>

<div class="landing">
  <a class="skip-link" href="#main-content">Skip to content</a>
  <header class="site">
    <div class="wrap bar">
      <a class="brand" href="/">
        <BrandMark size={30} decorative />
        <span>Rent<span class="accent">Ready</span>Pet</span>
      </a>
      <nav class="nav" aria-label="Primary">
        <a href="#do">What we do</a>
        <a href="#dont">What we don't</a>
        <a href="#why">Why us</a>
        <a href="/providers">For providers</a>
        <a class="btn" href="/register">Register your pet</a>
        <ThemeToggle />
      </nav>
    </div>
  </header>

  <main class="wrap" id="main-content" tabindex="-1">
    <section class="hero">
      <div class="hero-grid">
        <div>
          <p class="eyebrow">Pet-friendly renting, done right</p>
          <h1>New home. New adventures. Same best friend.</h1>
          <p class="sub">
            RentReadyPet is the kinder way to register your pet or assistance animal for a new home -
            a short, private, respectful process that's reviewed fairly. Finding a pet-friendly home
            should be a walk in the bark.
          </p>
          <div class="cta-row">
            <a class="btn lg" href="/register"
              >Register your pet <span aria-hidden="true">→</span></a
            >
            <a class="pm-link" href="#pm"
              >I'm a property manager <span aria-hidden="true">→</span></a
            >
          </div>
        </div>
        <img
          class="photo"
          src="/hero-new-home.webp"
          width="1672"
          height="941"
          alt="A golden retriever and a tabby cat among moving boxes in a bright new home, one box labeled New Home New Adventures"
          fetchpriority="high"
        />
      </div>

      <div class="band">
        <svg
          class="lock"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.7"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <rect x="4" y="10.5" width="16" height="10" rx="2" />
          <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
        </svg>
        <div>
          <h2>Your data stays yours.</h2>
          <p>
            We don't advertise to you, and we never sell your data - to anyone. Your registration
            exists to get you and your animal approved. That's it.
          </p>
        </div>
      </div>
    </section>

    <section class="blk" id="do">
      <p class="kicker">What we do</p>
      <h2>A calmer way to register a pet - or an assistance animal.</h2>
      <p class="lead">
        The same fair process for everyone, whether it's a golden retriever or a service dog.
      </p>
      <div class="cards">
        <div class="card">
          <h3>Guided registration</h3>
          <p>
            A short, plain-language form for pets and assistance animals. We ask only what's needed
            - and for assistance animals, only what the law allows.
          </p>
        </div>
        <div class="card">
          <h3>Upload &amp; track</h3>
          <p>
            Add documents securely and follow your request's status from your own portal. No phone
            tag, no mystery.
          </p>
        </div>
        <div class="card">
          <h3>A fair, consistent review</h3>
          <p>
            Everyone answers the same questions against the same standard, so your request is judged
            on a clear process - not a hunch or a bad day.
          </p>
        </div>
      </div>
    </section>

    <section class="blk" id="dont">
      <p class="kicker">What we don't do</p>
      <h2>Just as important as what we do.</h2>
      <ul class="dont">
        <li>
          <span class="x" aria-hidden="true">✕</span>
          <div>
            <b>We never ask about your disability.</b>
            <span
              >Only what the law permits - never a diagnosis, never proof of your condition.</span
            >
          </div>
        </li>
        <li>
          <span class="x" aria-hidden="true">✕</span>
          <div>
            <b>We don't make the housing decision.</b>
            <span
              >Your housing provider makes the call, not us. We just make sure the process behind it
              is fair and well documented.</span
            >
          </div>
        </li>
        <li>
          <span class="x" aria-hidden="true">✕</span>
          <div>
            <b>Assistance animals aren't pets.</b>
            <span
              >Service and support animals are protected under fair-housing law and are classified
              accordingly, never as pets.</span
            >
          </div>
        </li>
        <li class="privacy">
          <span class="x" aria-hidden="true">✕</span>
          <div>
            <b>We don't advertise to you, and we never sell your data - to anyone.</b>
            <span>No ads. No data brokers. No exceptions. It's the whole reason we built this.</span
            >
          </div>
        </li>
        <li>
          <span class="x" aria-hidden="true">✕</span>
          <div>
            <b>This isn't legal advice.</b>
            <span>We help you and your provider follow a fair process; we're not a law firm.</span>
          </div>
        </li>
      </ul>
    </section>

    <section class="blk">
      <p class="kicker">How it works</p>
      <h2>Three steps, and you're done.</h2>
      <div class="steps">
        <div class="step">
          <div class="n">1</div>
          <h3>Register your pet</h3>
          <p>Tell us about your animal and the home you're applying to. A few minutes, tops.</p>
        </div>
        <div class="step">
          <div class="n">2</div>
          <h3>Add a provider letter</h3>
          <p>
            Assistance animals only: upload your letter securely. We'll never ask about your
            disability.
          </p>
        </div>
        <div class="step">
          <div class="n">3</div>
          <h3>Track your status</h3>
          <p>
            Follow your request from your portal until it's approved. We'll email you at each step.
          </p>
        </div>
      </div>
    </section>

    <section class="blk vs" id="why">
      <p class="kicker">The honest comparison</p>
      <h2>Registering your pet shouldn't feel like an interrogation.</h2>
      <p class="lead">
        You've probably met the other kind of pet screening - clunky forms, invasive questions,
        surprise fees, and your data quietly sold off. Your best friend deserves a warmer welcome.
      </p>

      <div class="vs-cols">
        <section class="vs-card vs-legacy" aria-label="Legacy Software">
          <div class="vs-head">
            <p class="vs-kicker">The old way</p>
            <p class="vs-title">Legacy Software&trade;</p>
          </div>

          <div class="logo-wall" aria-hidden="true">
            <div class="lgo lgo-1">
              <span class="ico">🐾</span>
              <span class="nm">Paw<b>Trackr</b> 2000</span>
              <span class="tag">Y2K ready!</span>
            </div>
            <div class="lgo lgo-2">
              <span class="ico">📠</span>
              <span class="nm">ESA-Matic</span>
              <span class="tag">as seen on fax</span>
            </div>
            <div class="lgo lgo-3">
              <span class="ico">🦛</span>
              <span class="nm">ScreenHippo</span>
              <span class="tag">it just works&#42;</span>
            </div>
            <div class="lgo lgo-4">
              <span class="ico">🌐</span>
              <span class="nm">RentPetPro <span class="sfx">Suite&nbsp;Deluxe</span></span>
              <span class="tag">enterprise synergy</span>
            </div>
            <div class="lgo lgo-5">
              <span class="ico">💾</span>
              <span class="nm">Furm<em>Sign</em> '98</span>
              <span class="tag">please do not close this window</span>
            </div>
          </div>

          <ul class="vs-pts">
            <li><span class="mk miss" aria-hidden="true">✕</span> Treats you like a suspect, not a soon-to-be neighbor.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> Asks about your disability - something it isn't allowed to do.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> Surprise fees just to register your own pet.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> Buggy forms that forget your answers halfway through.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> Something breaks and there's no one to email or call.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> You never really know where your request stands.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> Your information quietly becomes someone's ad targeting.</li>
            <li><span class="mk miss" aria-hidden="true">✕</span> Painful on a phone, impossible with a screen reader.</li>
          </ul>
        </section>

        <section class="vs-card vs-us" aria-label="Rent Ready Pet">
          <div class="vs-head">
            <div class="vs-lockup">
              <BrandMark size={34} decorative />
              <span class="wm">Rent<span class="accent">Ready</span>Pet</span>
            </div>
          </div>

          <div class="trust-wall">
            <span class="trust">Only what the law allows</span>
            <span class="trust">No assistance-animal fees</span>
            <span class="trust">We never sell your data</span>
            <span class="trust">WCAG 2.2 AA accessible</span>
            <span class="trust wide">No ads, no data brokers, ever</span>
          </div>

          <ul class="vs-pts">
            <li><span class="mk hit" aria-hidden="true">✓</span> Treats you like a person - a short, plain-language welcome.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> Never asks about your disability. Only what the law allows.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> Assistance animals are never charged pet fees.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> Saves as you go, so your answers are never lost.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> Real help from real people when you need it.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> Track your request anytime from your own portal.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> We never sell your data and never show you ads.</li>
            <li><span class="mk hit" aria-hidden="true">✓</span> Smooth on your phone and built for screen readers (WCAG 2.2 AA).</li>
          </ul>
        </section>
      </div>

      <p class="vs-disclaimer">
        <b>Legacy Software&trade; is a composite of nobody in particular.</b>
        Any resemblance to real products is purely coincidental - and, honestly, a little bit their fault.
      </p>
    </section>

    <section class="blk" id="pm">
      <div class="pm">
        <div>
          <p class="pm-kicker">For property managers</p>
          <h2>Screening pets and assistance-animal requests?</h2>
          <p>
            Give your team a fair, consistent, well-documented process for every applicant -
            decision-support, never the decision-maker.
          </p>
        </div>
        <a class="btn gold" href="/providers">Get in touch <span aria-hidden="true">→</span></a>
      </div>
    </section>
  </main>

  <footer class="site foot">
    <div class="wrap foot-grid">
      <div>
        <div class="brand">
          <BrandMark size={28} decorative />
          <span>Rent<span class="accent">Ready</span>Pet</span>
        </div>
        <p>
          Finding a pet-friendly home? It's gonna be a <span class="pun">walk in the bark</span>.
          <span aria-hidden="true">🐾</span>
        </p>
      </div>
      <nav class="links" aria-label="Footer">
        <a href="/register">Register</a>
        <a href="#do">What we do</a>
        <a href="#dont">What we don't</a>
        <a href="/providers">Providers</a>
      </nav>
    </div>
    <p class="wrap legal">
      RentReadyPet provides decision-support for housing providers under the Fair Housing Act. It is
      not legal advice.
    </p>
  </footer>
</div>

<style>
  /* Warm surface tokens scoped to the landing; --brand/--gold come from app.css. */
  .landing {
    --l-ground: #fbf6ee;
    --l-panel: #ffffff;
    --l-ink: #2a211b;
    --l-muted: #6f6154;
    --l-line: #ecdfcd;
    /* Accessible dark gold for kicker text (the bright --gold fails contrast on
       the cream ground; 5.5:1 here vs 2.2:1). */
    --l-gold-ink: #8a5a0f;
    --l-maxw: 1080px;
    background: var(--l-ground);
    color: var(--l-ink);
    min-height: 100vh;
  }

  @media (prefers-color-scheme: dark) {
    .landing {
      --l-ground: var(--surface);
      --l-panel: var(--surface-2);
      --l-ink: var(--ink-strong);
      --l-muted: var(--muted);
      --l-line: var(--line);
      /* on the dark ground the bright gold is the readable one (dark gold fails) */
      --l-gold-ink: var(--gold);
    }
  }
  :root[data-theme='dark'] .landing {
    --l-ground: var(--surface);
    --l-panel: var(--surface-2);
    --l-ink: var(--ink-strong);
    --l-muted: var(--muted);
    --l-line: var(--line);
    --l-gold-ink: var(--gold);
  }
  :root[data-theme='light'] .landing {
    --l-ground: #fbf6ee;
    --l-panel: #ffffff;
    --l-ink: #2a211b;
    --l-muted: #6f6154;
    --l-line: #ecdfcd;
    --l-gold-ink: #8a5a0f;
  }

  /* Skip link: hidden until keyboard-focused, then pinned top-left. */
  .skip-link {
    position: absolute;
    left: 12px;
    top: -60px;
    z-index: 20;
    background: var(--brand);
    color: #fff;
    padding: 10px 16px;
    border-radius: 8px;
    text-decoration: none;
    transition: top 0.15s ease;
  }
  .skip-link:focus {
    top: 12px;
  }

  /* Anchor targets clear the sticky header when navigated to. */
  .landing :where([id]) {
    scroll-margin-top: 80px;
  }
  #main-content:focus {
    outline: none;
  }

  /* Visible keyboard focus on every interactive element. */
  .landing a:focus-visible {
    outline: 3px solid var(--link);
    outline-offset: 2px;
    border-radius: 6px;
  }

  @media (prefers-reduced-motion: reduce) {
    .skip-link {
      transition: none;
    }
  }
  .wrap {
    max-width: var(--l-maxw);
    margin: 0 auto;
    padding: 0 24px;
  }
  /* Generic link color, but never buttons - .btn sets its own high-contrast
     text (white on terracotta, dark on gold) and must win. */
  .landing a:not(.btn) {
    color: var(--link);
  }
  .landing h1,
  .landing h2,
  .landing h3 {
    text-wrap: balance;
    letter-spacing: -0.02em;
  }

  header.site {
    position: sticky;
    top: 0;
    z-index: 5;
    background: color-mix(in srgb, var(--l-ground) 88%, transparent);
    backdrop-filter: blur(8px);
    border-bottom: 1px solid var(--l-line);
  }
  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 800;
    font-size: 1.15rem;
    color: var(--l-ink);
    text-decoration: none;
  }
  .brand .accent {
    /* --link (5.6:1) not --brand (4.3:1) so the wordmark passes AA at its
       borderline size. */
    color: var(--link);
  }
  .nav {
    display: flex;
    align-items: center;
    gap: 22px;
    font-size: 0.92rem;
  }
  .nav a {
    color: var(--l-muted);
    text-decoration: none;
  }
  .nav a:hover {
    color: var(--l-ink);
  }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--brand);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 11px 18px;
    font-weight: 700;
    font-size: 0.95rem;
    cursor: pointer;
    text-decoration: none;
  }
  .btn:hover {
    background: var(--brand-hover);
  }
  .btn.lg {
    padding: 13px 22px;
    font-size: 1rem;
  }
  .btn.gold {
    background: var(--gold);
    color: #2a211b;
  }
  .btn.gold:hover {
    background: var(--surface-2);
  }
  .nav .btn {
    color: #fff;
  }

  .hero {
    padding: 56px 0 36px;
  }
  .hero-grid {
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    gap: 40px;
    align-items: center;
  }
  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-size: 0.72rem;
    font-weight: 800;
    color: var(--link);
    margin: 0 0 14px;
  }
  .hero h1 {
    font-size: clamp(2.3rem, 5.4vw, 3.4rem);
    line-height: 1.04;
    margin: 0 0 16px;
    font-weight: 800;
  }
  .hero .sub {
    font-size: 1.1rem;
    color: var(--l-muted);
    margin: 0 0 26px;
    max-width: 46ch;
  }
  .cta-row {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  .pm-link {
    font-size: 0.92rem;
    color: var(--l-muted);
    text-decoration: none;
  }
  .pm-link:hover {
    color: var(--l-ink);
  }
  .photo {
    width: 100%;
    height: auto;
    border-radius: 18px;
    border: 1px solid var(--l-line);
    display: block;
  }

  .band {
    margin-top: 25px;
    border-radius: 18px;
    padding: 26px 30px;
    background: linear-gradient(
      135deg,
      var(--brand-tint),
      color-mix(in srgb, var(--gold) 16%, var(--l-panel))
    );
    border: 1px solid var(--l-line);
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
  }
  .band .lock {
    width: 40px;
    height: 40px;
    flex: none;
    color: var(--brand);
  }
  .band h2 {
    margin: 0 0 4px;
    font-size: 1.25rem;
  }
  .band p {
    margin: 0;
    color: var(--l-muted);
    max-width: 70ch;
  }

  section.blk {
    padding: 44px 0;
  }
  .kicker {
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--l-gold-ink);
    margin: 0 0 10px;
  }
  .blk h2 {
    font-size: clamp(1.6rem, 3.5vw, 2.1rem);
    margin: 0 0 8px;
  }
  .blk .lead {
    color: var(--l-muted);
    margin: 0 0 26px;
    max-width: 56ch;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  .card {
    background: var(--l-panel);
    border: 1px solid var(--l-line);
    border-radius: 14px;
    padding: 22px;
  }
  .card h3 {
    margin: 0 0 6px;
    font-size: 1.08rem;
  }
  .card p {
    margin: 0;
    color: var(--l-muted);
    font-size: 0.95rem;
  }

  .dont {
    background: var(--l-panel);
    border: 1px solid var(--l-line);
    border-radius: 16px;
    padding: 6px 26px;
    margin: 0;
    list-style: none;
  }
  .dont li {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 18px 0;
    border-bottom: 1px solid var(--l-line);
  }
  .dont li:last-child {
    border-bottom: none;
  }
  .dont .x {
    width: 26px;
    height: 26px;
    flex: none;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: var(--brand-tint);
    color: var(--brand);
    font-weight: 800;
    margin-top: 2px;
  }
  .dont b {
    display: block;
    font-size: 1.02rem;
  }
  .dont span {
    color: var(--l-muted);
    font-size: 0.94rem;
  }
  .dont li.privacy {
    background: linear-gradient(90deg, var(--brand-tint), transparent);
    border-radius: 10px;
    padding-left: 12px;
    padding-right: 12px;
  }

  .steps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
  }
  .step {
    position: relative;
    background: var(--l-panel);
    border: 1px solid var(--l-line);
    border-radius: 14px;
    padding: 22px;
  }
  .step .n {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: var(--brand);
    color: #fff;
    display: grid;
    place-items: center;
    font-weight: 800;
    margin-bottom: 12px;
  }
  .step h3 {
    margin: 0 0 6px;
    font-size: 1.05rem;
  }
  .step p {
    margin: 0;
    color: var(--l-muted);
    font-size: 0.94rem;
  }

  .pm {
    border-radius: 18px;
    padding: 34px;
    border: 1px solid var(--l-line);
    background: var(--l-ink);
    color: var(--l-ground);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }
  .pm-kicker {
    margin: 0 0 8px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 0.72rem;
    font-weight: 800;
    /* The .pm card inverts between themes, so tint the card's own text color
       rather than a fixed accent that would fail on one ground. */
    color: color-mix(in srgb, var(--l-ground) 78%, transparent);
  }
  .pm h2 {
    margin: 0 0 6px;
    font-size: 1.5rem;
    color: var(--l-ground);
  }
  .pm p {
    margin: 0;
    color: color-mix(in srgb, var(--l-ground) 72%, transparent);
    max-width: 52ch;
  }

  footer.foot {
    border-top: 1px solid var(--l-line);
    padding: 34px 0 48px;
    margin-top: 20px;
  }
  .foot-grid {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
  }
  /* Scope to the grid so it doesn't out-specify .legal (which is a .wrap p
     outside the grid) and force the disclaimer into a narrow, off-center block. */
  .foot-grid p {
    color: var(--l-muted);
    font-size: 0.88rem;
    margin: 8px 0 0;
    max-width: 48ch;
  }
  .foot .pun {
    color: var(--link);
    font-weight: 600;
  }
  .links {
    display: flex;
    gap: 20px;
    font-size: 0.9rem;
  }
  .links a {
    color: var(--l-muted);
    text-decoration: none;
  }
  .links a:hover {
    color: var(--l-ink);
  }
  .legal {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--l-line);
    text-align: center;
    font-size: 0.78rem;
    color: var(--l-muted);
  }

  /* ---- Why us: the tongue-in-cheek "vs Legacy Software" comparison ---- */
  /* Cool, drab villain palette scoped to the section - deliberately off-brand
     so the contrast with our warm side carries the joke. Themed like app.css. */
  .vs {
    --vs-panel: #eef1f4;
    --vs-line: #cfd6de;
    --vs-slate: #55606f;
    --vs-miss: #a2402f;
  }
  @media (prefers-color-scheme: dark) {
    .vs {
      --vs-panel: #1f242b;
      --vs-line: #363f4a;
      --vs-slate: #9aa6b6;
      --vs-miss: #e59182;
    }
  }
  :root[data-theme='dark'] .vs {
    --vs-panel: #1f242b;
    --vs-line: #363f4a;
    --vs-slate: #9aa6b6;
    --vs-miss: #e59182;
  }
  :root[data-theme='light'] .vs {
    --vs-panel: #eef1f4;
    --vs-line: #cfd6de;
    --vs-slate: #55606f;
    --vs-miss: #a2402f;
  }

  .vs-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
    align-items: stretch;
  }
  .vs-card {
    border-radius: 16px;
    padding: 22px 24px;
    display: flex;
    flex-direction: column;
  }
  /* Both columns stretch to equal height; our shorter list centers in the slack
     so the pair reads balanced instead of leaving a stubby gap. */
  .vs-us .vs-pts {
    margin-block: auto;
  }
  .vs-legacy {
    background: var(--vs-panel);
    border: 1px solid var(--vs-line);
  }
  .vs-us {
    background: var(--l-panel);
    border: 1px solid color-mix(in srgb, var(--brand) 32%, var(--l-line));
  }
  .vs-head {
    min-height: 44px;
    margin-bottom: 16px;
  }
  .vs-kicker {
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 0.68rem;
    font-weight: 800;
    color: var(--vs-slate);
  }
  .vs-title {
    margin: 2px 0 0;
    font-weight: 800;
    font-size: 1.05rem;
    color: var(--vs-slate);
  }
  .vs-lockup {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-weight: 800;
    font-size: 1.2rem;
    color: var(--l-ink);
  }
  .vs-lockup .accent {
    color: var(--link);
  }

  /* the wall of clip-art - each fake brand is its own little crime */
  .logo-wall {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 18px;
  }
  .lgo {
    background: #fff;
    border: 1px solid var(--vs-line);
    border-radius: 8px;
    padding: 10px 8px;
    min-height: 74px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    text-align: center;
  }
  .lgo .ico {
    font-size: 1.4rem;
    line-height: 1;
  }
  .lgo .nm {
    font-size: 0.82rem;
    line-height: 1.05;
  }
  .lgo .tag {
    font-size: 0.55rem;
    opacity: 0.85;
  }
  .lgo-1 {
    font-family: 'Comic Sans MS', 'Comic Sans', 'Chalkboard SE', cursive;
  }
  .lgo-1 .nm {
    color: #17559e;
    font-weight: 700;
  }
  .lgo-1 .nm b {
    color: #d0201a;
  }
  .lgo-1 .tag {
    color: #17559e;
  }
  .lgo-2 {
    background: linear-gradient(#dfe7ff, #b9c8ff);
    border-color: #8ea3e6;
  }
  .lgo-2 .nm {
    font-family: Georgia, 'Times New Roman', serif;
    font-weight: 800;
    font-style: italic;
    background: linear-gradient(#ffd400, #ff2d2d);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    -webkit-text-stroke: 0.5px #7a1a1a;
  }
  .lgo-2 .tag {
    color: #2f7a2f;
    font-weight: 700;
  }
  .lgo-3 {
    background: repeating-linear-gradient(45deg, #fff, #fff 8px, #fff5d6 8px, #fff5d6 16px);
  }
  .lgo-3 .nm {
    font-family: 'Papyrus', 'Bradley Hand', fantasy;
    color: #0a7d3b;
    font-weight: 700;
  }
  .lgo-3 .tag {
    color: #c0392b;
  }
  .lgo-4 {
    background: linear-gradient(#f4f6f9, #d7dde6);
    border-color: #b6c0cd;
  }
  .lgo-4 .nm {
    font-weight: 800;
    color: #34404f;
    letter-spacing: -0.02em;
  }
  .lgo-4 .nm .sfx {
    color: #7c8a9c;
    font-weight: 600;
  }
  .lgo-4 .tag {
    color: #6b7686;
    font-style: italic;
  }
  .lgo-5 {
    grid-column: 1 / -1;
    flex-direction: row;
    gap: 8px;
    font-family: 'Comic Sans MS', 'Comic Sans', 'Chalkboard SE', cursive;
    background:
      radial-gradient(circle at 12% 50%, #ffe0ec 0 18%, transparent 19%),
      linear-gradient(90deg, #fff, #eafff1);
  }
  .lgo-5 .nm {
    color: #b0179e;
    font-weight: 700;
  }
  .lgo-5 .nm em {
    color: #1a4fd6;
    font-style: normal;
  }
  .lgo-5 .tag {
    color: #7a7a7a;
  }
  @media (prefers-reduced-motion: no-preference) {
    .lgo {
      transition: transform 0.15s ease;
    }
    .vs-legacy:hover .lgo:hover {
      transform: rotate(-2deg) scale(1.03);
    }
  }

  /* Our answer to the clip-art wall: real credentials, same footprint, calm tone. */
  .trust-wall {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 18px;
  }
  .trust {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    text-align: center;
    min-height: 44px;
    padding: 8px 12px;
    border-radius: 8px;
    background: var(--brand-tint);
    border: 1px solid color-mix(in srgb, var(--brand) 22%, var(--l-line));
    color: var(--l-ink);
    font-weight: 700;
    font-size: 0.82rem;
    line-height: 1.15;
  }
  .trust::before {
    content: '✓';
    color: var(--brand);
    font-size: 0.72rem;
    font-weight: 800;
  }
  .trust.wide {
    grid-column: 1 / -1;
  }

  .vs-pts {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .vs-pts li {
    display: flex;
    gap: 11px;
    align-items: flex-start;
    font-size: 0.94rem;
    line-height: 1.4;
  }
  .vs-legacy .vs-pts li {
    color: var(--vs-slate);
  }
  .vs-us .vs-pts li {
    color: var(--l-ink);
  }
  .mk {
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    font-size: 0.72rem;
    font-weight: 800;
    margin-top: 1px;
  }
  .mk.hit {
    background: var(--ok-bg);
    color: var(--ok-ink);
  }
  .mk.miss {
    color: var(--vs-miss);
    border: 1.5px solid color-mix(in srgb, var(--vs-miss) 45%, transparent);
  }
  .vs-disclaimer {
    margin: 22px 0 0;
    font-size: 0.82rem;
    color: var(--l-muted);
    max-width: 62ch;
    line-height: 1.5;
  }
  .vs-disclaimer b {
    color: var(--l-ink);
  }

  /* Interactive lift on cards (pointer devices only). */
  @media (hover: hover) {
    .card,
    .step {
      transition:
        transform 0.16s ease,
        box-shadow 0.16s ease,
        border-color 0.16s ease;
    }
    .card:hover,
    .step:hover {
      transform: translateY(-3px);
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
      border-color: color-mix(in srgb, var(--brand) 40%, var(--l-line));
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .card:hover,
    .step:hover {
      transform: none;
    }
  }

  /* Connector between the numbered steps (desktop 3-up only); sits in the grid
     gap at the height of the number circles. */
  @media (min-width: 821px) {
    .step:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 39px;
      right: -18px;
      width: 18px;
      height: 2px;
      background: color-mix(in srgb, var(--brand) 35%, transparent);
    }
  }

  /* Phone: collapse the in-page section links so the header does not crowd the
     brand and toggle; brand + Register + toggle remain (these links also live in
     the footer). */
  @media (max-width: 760px) {
    .nav {
      gap: 14px;
    }
    .nav a:not(.btn) {
      display: none;
    }
  }

  @media (max-width: 820px) {
    .hero-grid,
    .cards,
    .steps,
    .vs-cols {
      grid-template-columns: 1fr;
    }
    .hero-grid img.photo {
      order: -1;
    }
  }
</style>
